// Чистые функции раздела «Табель РБ»
import { MONTH_NAMES, PLAN_LABELS, REASON_MAX, REASON_MIN } from './constants';
import { CyclePlan, RunStatus, SyncResult, TimesheetRun } from './types';

/**
 * Дата и время события. Сервер отдаёт момент времени в ISO, поэтому показываем его в часовом поясе браузера
 * (общий fmtDateTime документов выводит в UTC и здесь дал бы сдвиг).
 */
export function fmtMoment(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Дата без времени (YYYY-MM-DD от сервера) как ДД.ММ.ГГГГ; некорректное значение - «-» */
export function fmtDay(day?: string | null): string {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '-';
}

/** Дата YYYY-MM-DD плюс n дней (в UTC, без сдвигов часового пояса) */
export function addDays(day: string, n: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n)).toISOString().slice(0, 10);
}

/** «12-01» -> «1 декабря» */
export function fmtMonthDay(value: string): string {
  const m = /^(\d{2})-(\d{2})$/.exec(value);
  if (!m || Number(m[1]) < 1 || Number(m[1]) > 12) return value;
  return `${Number(m[2])} ${MONTH_NAMES[Number(m[1]) - 1]}`;
}

/** Причина как на сервере: пробелы схлопываются, края обрезаются */
export const normalizeReason = (text: string): string => text.replace(/\s+/g, ' ').trim();

export const isReasonValid = (text: string): boolean => {
  const length = normalizeReason(text).length;
  return length >= REASON_MIN && length <= REASON_MAX;
};

/** Ближайшее действие автоматизации одной строкой */
export function planText(plan: CyclePlan): string {
  return (PLAN_LABELS[plan.action] || PLAN_LABELS.none).replace('{year}', plan.year ? String(plan.year) : '');
}

/** Доля выполненного в процентах (0-100) */
export function percent(done: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

export const isRunning = (run: TimesheetRun | null): boolean => Boolean(run) && (run as TimesheetRun).status === 'running';

/** Запуск закончился не успехом и требует внимания оператора */
export const isRunProblem = (status: RunStatus): boolean => status === 'failed' || status === 'interrupted';

/** Итог запуска одной строкой: «создано 12, уже были 3, ошибок 1» */
export function runTotals(run: TimesheetRun): string {
  const parts = [`создано ${run.created}`, `уже были ${run.existing}`];
  if (run.failed > 0) parts.push(`ошибок ${run.failed}`);
  return parts.join(', ');
}

/** Итог сверки с папкой для уведомления */
export function syncMessage(r: SyncResult): string {
  const parts = [`файлов в папке: ${r.onDrive}`, `привязано к аптекам: ${r.matched}`];
  if (r.added > 0) parts.push(`добавлено: ${r.added}`);
  if (r.updated > 0) parts.push(`обновлено: ${r.updated}`);
  if (r.removed > 0) parts.push(`убрано: ${r.removed}`);
  if (r.duplicates > 0) parts.push(`дублей: ${r.duplicates}`);
  return `Сверка завершена. ${parts.join(', ')}`;
}

/** Допустимый идентификатор папки Google Drive (как на сервере): латиница, цифры, «-» и «_», 10-100 символов */
export const isFolderId = (value: string): boolean => /^[A-Za-z0-9_-]{10,100}$/.test(value.trim());

/**
 * Идентификатор папки из того, что вставили: сама строка либо ссылка вида
 * https://drive.google.com/drive/folders/<id>?usp=sharing. Некорректное значение возвращается как есть - его отклонит валидация.
 */
export function extractFolderId(input: string): string {
  const text = input.trim();
  const match = /\/folders\/([A-Za-z0-9_-]+)/.exec(text) || /[?&]id=([A-Za-z0-9_-]+)/.exec(text);
  return match ? match[1] : text;
}
